import { csvFilename, csvHours, csvMoney, downloadCsv, type CsvCell } from '../../lib/csv';
import type { ApprovalRow, WeekData } from './api';

/**
 * The timesheet's "Export CSV".
 *
 * /time was the last data screen with no export — the one Harvest users take to
 * a spreadsheet every Friday. It follows the same rules as every other export
 * (see `lib/csv.ts`): it contains what is on screen, it is named
 * `timesheet-<person>-<org>-<week>.csv`, its cells are data not typography, and
 * its last row is the footing the grid prints.
 *
 * ONE ROW PER ENTRY, not one per cell: a cell can hold several entries with
 * different notes and different bill rates, and a file that flattened them
 * would lose exactly the detail a reviewer opens the export to read. The day
 * columns stay on screen; the file carries the date.
 */
export function exportWeekCsv(week: WeekData): void {
  const rows: CsvCell[][] = [
    [
      'Date',
      'Person',
      'Client',
      'Agreement',
      'Task',
      'Hours',
      'Billable',
      'Bill rate',
      'Billable value',
      'Notes',
      'Status',
    ],
  ];
  let hours = 0;
  let value = 0;
  for (const row of week.rows) {
    for (const entry of row.entries) {
      // Deleted-in-Harvest hours are struck through on screen and can never be
      // invoiced, so their VALUE is zero here too — the file must not imply
      // money the invoice wizard would refuse to offer.
      const gone = entry.deletedInSource;
      const rate = entry.billRate;
      // Same valuation as the day row, the week summary and the approvals
      // roster: billable, still existing, and on an agreement billed BY THE HOUR.
      // An hourly valuation of fee-based work is money nobody may invoice, and
      // a spreadsheet is exactly where such a number gets summed and believed.
      const lineValue =
        !gone && entry.billable && rate != null && row.agreement.billBy === 'HOURS'
          ? entry.hours * rate
          : 0;
      hours += entry.hours;
      value += lineValue;
      rows.push([
        entry.date,
        week.person.name,
        row.agreement.client.name,
        row.agreement.name,
        entry.task?.name ?? row.task?.name ?? '',
        csvHours(entry.hours),
        entry.billable ? 'yes' : 'no',
        rate != null ? csvMoney(rate) : '',
        csvMoney(lineValue),
        entry.notes ?? '',
        gone
          ? 'deleted in Harvest'
          : entry.locked
            ? 'invoiced'
            : entry.lockedInSource
              ? 'locked in Harvest'
              : // Per DAY: a week can straddle an approved month and an open one.
                (week.dayStatus?.[entry.date]?.status ?? week.timesheet.status).toLowerCase(),
      ]);
    }
  }
  // ⚠ IN A FILE, "ROWS" MEANS FILE ROWS. This footed "4 rows" over six data
  // lines because it was counting GRID rows (agreement × task) — a number that
  // is meaningless once the file is open and looks like a miscount. The file
  // is one row per ENTRY, so the footing counts entries.
  const entryCount = rows.length - 1;
  rows.push([
    `Week of ${week.weekStart} — ${entryCount} entr${entryCount === 1 ? 'y' : 'ies'}`,
    '',
    '',
    '',
    '',
    csvHours(hours),
    '',
    '',
    csvMoney(value),
    '',
    '',
  ]);
  downloadCsv(
    csvFilename({
      entity: 'timesheet',
      view: week.person.name,
      period: week.weekStart,
    }),
    rows,
  );
}

/** Which part of the Approvals queue a row was printed in. */
export type ApprovalsSection = 'Waiting for review' | 'Not submitted' | 'Recently decided';

/**
 * The Approvals queue, exactly as it is on screen: one row per PERIOD (a day,
 * a week or a month — whatever that person submits), in the screen's order,
 * under the current filter.
 */
export function exportApprovalsCsv(
  rows: Array<{ section: ApprovalsSection; row: ApprovalRow }>,
  view: string,
  today: string,
): void {
  // ⚠ The footing sums THESE rows, never `summary` — the caller hands us the
  // FILTERED set, and the file must foot to the screen that produced it.
  const sum = (pick: (r: ApprovalRow) => number) =>
    rows.reduce((total, { row }) => total + pick(row), 0);
  const cells: CsvCell[][] = [
    [
      'Section',
      'Person',
      'Type',
      'Frequency',
      'Period',
      'Period start',
      'Period end',
      'Status',
      'Submitted',
      'Reviewed',
      'Reviewed by',
      'Tracked hours',
      'Billable hours',
      'Billable value',
      'Fee work hours',
      'Days tracked',
      'Cost',
      'Cost basis',
      'Hours with no cost rate',
      'Owner note',
      'Reviewer note',
    ],
    ...rows.map(({ section, row: r }) => [
      section,
      r.person.name,
      r.person.type === 'CONTRACTOR' ? 'Contractor' : 'Staff',
      r.person.cadence.toLowerCase(),
      r.label,
      r.periodStart,
      r.periodEnd,
      r.status.toLowerCase(),
      r.submittedAt ?? '',
      r.reviewedAt ?? '',
      r.reviewedBy?.name ?? '',
      csvHours(r.hours),
      csvHours(r.billableHours),
      csvMoney(r.billableAmount),
      csvHours(r.feeWorkHours),
      String(r.daysTracked),
      r.cost === null ? '' : csvMoney(r.cost),
      r.costBasis === 'salary' ? 'salaried' : r.costBasis === 'no-rate' ? 'no cost rate' : 'hourly',
      csvHours(r.uncostedHours),
      r.note ?? '',
      r.reviewNote ?? '',
    ]),
    [
      `${rows.length} timesheet${rows.length === 1 ? '' : 's'}`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      csvHours(sum((r) => r.hours)),
      csvHours(sum((r) => r.billableHours)),
      csvMoney(sum((r) => r.billableAmount)),
      csvHours(sum((r) => r.feeWorkHours)),
      '',
      csvMoney(sum((r) => r.cost ?? 0)),
      '',
      csvHours(sum((r) => r.uncostedHours)),
      '',
      '',
    ],
  ];
  downloadCsv(csvFilename({ entity: 'timesheets', view, period: today }), cells);
}
